import { LogData, LogEventGrouping, LogLevel } from 'shared/types/logger';
import { isKnownValue } from 'shared/domain/currentUser/currentUserIdentity';
import { isSentryEnabled } from 'shared/utils/environment/variables';

interface SentryInstance {
  withScope: (callback: (scope: any) => void) => void;
  captureException: (error: unknown) => void;
  captureMessage: (message: string) => void;
}

const SENTRY_GLOBAL_WINDOW_MS = 60_000;
const SENTRY_GLOBAL_MAX_EVENTS_PER_WINDOW = 30;
const recentSentryEventTimestamps: number[] = [];

function isGlobalRateLimited(now: number): boolean {
  while (
    recentSentryEventTimestamps.length > 0 &&
    now - recentSentryEventTimestamps[0] > SENTRY_GLOBAL_WINDOW_MS
  ) {
    recentSentryEventTimestamps.shift();
  }

  if (
    recentSentryEventTimestamps.length >=
    SENTRY_GLOBAL_MAX_EVENTS_PER_WINDOW
  ) {
    return true;
  }

  recentSentryEventTimestamps.push(now);
  return false;
}

interface SerializedErrorPayload extends Record<string, unknown> {
  name?: string;
  message: string;
  stack?: string;
}

/**
 * Rebuilds an Error from its log serialization (`serializeErrorForLog`),
 * keeping the custom fields (e.g. `entityName` and `diagnostics` of a request
 * timeout), so `beforeSend` can fingerprint it like the original instance.
 */
function restoreSerializedError({
  name,
  message,
  stack,
  ...customFields
}: SerializedErrorPayload): Error {
  const error = Object.assign(new Error(message), customFields);
  if (name) {
    error.name = name;
  }
  if (stack) {
    error.stack = stack;
  }
  return error;
}

function isSerializedError(value: unknown): value is SerializedErrorPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { message?: unknown }).message === 'string'
  );
}

function toError(value: unknown): Error | undefined {
  if (value instanceof Error) {
    return value;
  }
  return isSerializedError(value) ? restoreSerializedError(value) : undefined;
}

/**
 * The error to capture and the remaining details. Accepts the error itself
 * or `{ error, ...details }`, the shape many callers log, so both are
 * captured as exceptions and fingerprinted alike.
 */
function splitErrorFromLogDetails(payload: unknown): {
  error: Error | undefined;
  details: unknown;
} {
  const error = toError(payload);
  if (error) {
    return { error, details: undefined };
  }
  if (typeof payload === 'object' && payload !== null && 'error' in payload) {
    const { error: nestedError, ...details } = payload as { error: unknown };
    const nested = toError(nestedError);
    if (nested) {
      return { error: nested, details };
    }
  }
  return { error: undefined, details: payload };
}

/**
 * Everything the logger flattened into the log besides its envelope
 * (`sharedLog` spreads `additionalInfo` and the device info into the log).
 */
function getLogContext({
  msg: _msg,
  level: _level,
  user_id: _userId,
  user_email: _userEmail,
  logId: _logId,
  full_error_object: _fullErrorObject,
  additionalInfo: _additionalInfo,
  grouping: _grouping,
  ...context
}: LogData): Record<string, unknown> {
  return context;
}

/** Explicit grouping of the log, with the entity it is about as a tag. */
function getEventGrouping(data: LogData): LogEventGrouping {
  const { entityName } = getLogContext(data);
  const entityTag: Record<string, string> =
    typeof entityName === 'string' ? { entity: entityName } : {};
  return {
    fingerprint: data.grouping?.fingerprint,
    tags: { ...entityTag, ...data.grouping?.tags },
  };
}

/**
 * Shared Sentry logging function that works with both @sentry/react and @sentry/browser
 * @param sentry - Sentry instance (from @sentry/react or @sentry/browser)
 * @param data - Log data to send to Sentry
 */
export function sentryLog(sentry: SentryInstance, data: LogData): void {
  if (!isSentryEnabled()) return;

  // INFO goes to the console only: consoleLoggingIntegration captures warn
  // and error (initSentryInApp, initSentryInSw), so info never reaches Sentry.
  if (data.level === LogLevel.INFO) {
    const logMessage = `[${data.logId || 'INFO'}] ${data.msg}`;
    const logData = {
      additionalInfo: data.additionalInfo,
      user_email: data.user_email,
      user_id: data.user_id,
      logId: data.logId,
    };
    // eslint-disable-next-line no-console
    console.log(logMessage, logData);
    return;
  }

  // Only events count against the limit: info logs must not crowd out errors.
  if (isGlobalRateLimited(Date.now())) return;

  sentry.withScope((scope) => {
    scope.setLevel(data.level);
    // The SW has no global Sentry user, so attach the one known to the logger.
    if (isKnownValue(data.user_id)) {
      scope.setUser({
        id: data.user_id,
        ...(isKnownValue(data.user_email) ? { email: data.user_email } : {}),
      });
    }
    scope.setExtras({
      message: data.msg,
      additionalInfo: data.additionalInfo,
      logId: data.logId,
    });
    scope.setContext('log', getLogContext(data));
    const { fingerprint, tags } = getEventGrouping(data);
    if (tags) {
      scope.setTags(tags);
    }
    if (fingerprint) {
      scope.setFingerprint(fingerprint);
    }

    const { error, details } = splitErrorFromLogDetails(data.full_error_object);
    if (details !== undefined) {
      scope.setExtra('error_payload', details);
    }
    if (error) {
      sentry.captureException(error);
    } else {
      sentry.captureMessage(data.msg);
    }
  });
}
